import type { FC } from 'react';
import { useState, useCallback } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { EditTagsAutocomplete } from '../../tags';
import { format } from 'date-fns';
import useChangeCreativeComment from '../hooks/useChangeCreativeComment';
import useChangeCreativeTags from '../hooks/useChangeCreativeTags';
import type { CreativeTag } from '../api/types';
import { CreativeAccordion, CreativeAccordionDetails, CreativeAccordionSummary } from './CreativeComponents';
import { ExpandMoreOutlined } from '@mui/icons-material';

interface CreativeTagsFormProps {
  creativeId: number;
  comment: string | null;
  tags: CreativeTag[];
  updatedAt: Date;
  isExpanded?: boolean;
}

const CreativeTagsForm: FC<CreativeTagsFormProps> = ({ isExpanded, creativeId, comment, tags, updatedAt }) => {
  const [expanded, setExpanded] = useState(!!isExpanded);

  const {
    error: commentError,
    comment: commentUpdated,
    updateComment,
    mutation: commentMutation,
  } = useChangeCreativeComment(creativeId, comment);

  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateComment(event.target.value);
    },
    [updateComment]
  );

  const {
    error: tagsError,
    tags: tagsAutocomplete,
    updateTags,
    mutation: tagsMutation,
  } = useChangeCreativeTags(
    creativeId,
    tags.map((t) => ({ label: t.tag_name, groupId: t.group_id, id: t.tag_id }))
  );

  const handleExpansion = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, [setExpanded]);

  return (
    <CreativeAccordion
      sx={(t) => ({
        width: '100%',
        pb: 1.25,
        borderBottom: `1px solid ${t.palette.divider}`,
        '&.MuiPaper-root': { borderRadius: '0' },
      })}
      expanded={expanded}
      onChange={handleExpansion}
    >
      <CreativeAccordionSummary
        sx={{ width: '100%', '& .MuiAccordionSummary-content': { justifyContent: 'space-between' } }}
      >
        <Typography>Tags and comment</Typography>
        <ExpandMoreOutlined sx={{ transition: 'transform 0.2s', transform: `rotate(${expanded ? 180 : 0}deg)` }} />
      </CreativeAccordionSummary>
      <CreativeAccordionDetails sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'start',
            gap: 2,
          }}
        >
          <EditTagsAutocomplete
            tags={tagsAutocomplete}
            handleChangeTags={updateTags}
            helperText={tagsMutation.isPending ? 'Saving...' : tagsError ?? ''}
          />
          <TextField
            multiline
            minRows={5}
            fullWidth
            label="Comment"
            value={commentUpdated}
            onChange={handleCommentChange}
            error={Boolean(commentError)}
            helperText={
              commentError ??
              (commentMutation.isPending ? `Saving...` : `Saved at: ${format(updatedAt, 'dd.MM.yyyy HH:mm')}`)
            }
          />
        </Box>
      </CreativeAccordionDetails>
    </CreativeAccordion>
  );
};

export default CreativeTagsForm;
